/**
 * Year6ReadyPage — /year6/:slug/ready
 *
 * Launch button for teachers after lead submission. Big QR, share buttons,
 * £99 memory book reminder. Opens Mission Screen in a new tab.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { toast } from 'sonner';
import { WhatsappLogo, Copy, EnvelopeSimple, MonitorPlay, ArrowRight, Sparkle, Eye, UsersThree, CheckCircle } from '@phosphor-icons/react';
import { LowContentNudgeBanner } from '../components/Year6LowContentRecovery';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Year6ReadyPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const unlocked = searchParams.get('unlocked') === '1';
  const [event, setEvent] = useState(null);
  const [stats, setStats] = useState(null);
  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}/e/${slug}` : '';
  const screenUrl = typeof window !== 'undefined' ? `${window.location.origin}/year6/${slug}/screen` : '';
  const controlUrl = typeof window !== 'undefined' ? `${window.location.origin}/year6/${slug}/control` : '';

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = unlocked ? 8 : 1;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/events/slug/${slug}`);
        if (cancelled) return;
        setEvent(r.data);
        // Fetch low-content stats once we have the event_id (don't block rendering on it)
        if (r.data?.event_id) {
          axios.get(`${API}/events/${r.data.event_id}/year6/stats`)
            .then((s) => { if (!cancelled) setStats(s.data); })
            .catch(() => { /* ignore */ });
        }
        if (unlocked && !r.data?.memory_book_purchased && attempts < maxAttempts) {
          attempts += 1;
          setTimeout(tick, 1500);
        }
      } catch { /* ignore */ }
    };
    tick();
    return () => { cancelled = true; };
  }, [slug, unlocked]);

  const copy = async (v, label = 'Link copied') => {
    try { await navigator.clipboard.writeText(v); toast.success(label); }
    catch { toast.error('Could not copy'); }
  };

  const waText = `We're making a Year 6 Memory Book 🎓\nUpload your photos & messages here:\n${shareUrl}`;
  const waUrl = `https://wa.me/?text=${encodeURIComponent(waText)}`;
  const mailUrl = `mailto:?subject=${encodeURIComponent('Our Year 6 Leavers page')}&body=${encodeURIComponent(waText)}`;

  return (
    <div className="min-h-screen bg-neutral-950 text-white" data-testid="year6-ready-page">
      <div className="max-w-xl mx-auto px-5 pt-10 pb-20">
        {unlocked && (
          <div className="mb-5 rounded-2xl p-4 border" style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.14), rgba(16,185,129,0.04))', borderColor: 'rgba(16,185,129,0.35)' }} data-testid="year6-ready-unlocked-banner">
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-9 h-9 rounded-full bg-emerald-400/15 border border-emerald-400/35 flex items-center justify-center">
                <CheckCircle size={18} weight="fill" className="text-emerald-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-300 mb-0.5">Memory Book unlocked 🎉</p>
                <p className="text-white text-sm font-bold leading-tight mb-0.5">Payment received — your Memory Book is ready to download once memories are added.</p>
                <p className="text-white/55 text-[12px]">Now share your class page below so kids can start uploading.</p>
              </div>
            </div>
          </div>
        )}
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-300/10 border border-amber-300/30 text-amber-200 text-[10px] font-black uppercase tracking-[0.18em] mb-4">
          <Sparkle size={11} weight="fill" /> 🎓 Your class page is ready
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl font-black leading-tight mb-2" data-testid="year6-ready-title">
          {event?.event_name || 'Your Year 6 Leavers'}
        </h1>
        <p className="text-white/60 text-sm mb-8">Share this with your class now — they can start uploading memories instantly.</p>

        {/* QR */}
        <div className="bg-white rounded-3xl p-6 flex flex-col items-center mb-6" data-testid="year6-ready-qr-card">
          <QRCodeSVG value={shareUrl} size={224} bgColor="#FFFFFF" fgColor="#0F1014" includeMargin={false} />
          <p className="text-neutral-600 text-[11px] font-black uppercase tracking-[0.18em] mt-3">Scan to join</p>
        </div>

        {/* Low-content soft nudge — only when low and not purchased */}
        {stats && !stats.purchased && stats.low_content && (
          <LowContentNudgeBanner slug={slug} />
        )}

        {/* Share actions */}
        <div className="space-y-2.5 mb-6">
        <a href={waUrl} target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-sm font-black text-white" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }} data-testid="year6-ready-whatsapp">
            <WhatsappLogo size={18} weight="fill" /> Share on WhatsApp
          </a>
          <button onClick={() => copy(shareUrl, 'Link copied — paste into your class WhatsApp group')} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-bold bg-white text-neutral-900 hover:bg-neutral-100" data-testid="year6-ready-copy">
            <Copy size={16} weight="bold" /> Copy link
          </button>
          <a href={mailUrl} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-bold bg-white/[0.04] border border-white/10 text-white hover:bg-white/[0.08]" data-testid="year6-ready-email">
            <EnvelopeSimple size={16} weight="bold" /> Send via email
          </a>
          <p className="text-[12px] text-white/45 text-center break-all pt-1" data-testid="year6-ready-link">{shareUrl}</p>
        </div>

        {/* Parent share — distribution loop (1 teacher → 30 parents) */}
        <ParentShareCard event={event} slug={slug} shareUrl={shareUrl} />

        {/* Mission Screen launch */}
        <div className="rounded-2xl p-5 mb-4" style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.12), rgba(139,92,246,0.06))', border: '1px solid rgba(139,92,246,0.25)' }} data-testid="year6-ready-screen-card">
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#C29D52]/85 mb-2 inline-flex items-center gap-1.5">
            <MonitorPlay size={11} weight="fill" /> For the classroom projector
          </p>
          <h3 className="font-bold text-lg mb-1">Open the Mission Screen</h3>
          <p className="text-white/60 text-sm mb-3">Full-screen rotating missions + live QR. Kids scan and upload as missions appear.</p>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => window.open(screenUrl, '_blank')} className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-black text-white" style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }} data-testid="year6-ready-open-screen">
              <MonitorPlay size={13} weight="fill" /> Open screen
            </button>
            <button onClick={() => navigate(`/year6/${slug}/control`)} className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold border border-white/15 bg-white/[0.04] hover:bg-white/[0.08]" data-testid="year6-ready-open-control">
              Teacher controls <ArrowRight size={12} weight="bold" />
            </button>
          </div>
        </div>

        {/* Preview */}
        <button onClick={() => window.open(`/e/${slug}`, '_blank')} className="w-full text-[12px] text-white/55 hover:text-white/80 underline underline-offset-2 inline-flex items-center justify-center gap-1.5 mb-6" data-testid="year6-ready-preview">
          <Eye size={12} weight="fill" /> Preview student view
        </button>

        {/* Memory Book price reminder */}
        <div className="rounded-2xl border-t border-white/8 pt-5 text-center">
          <p className="text-[11px] font-black uppercase tracking-[0.16em] text-amber-300 mb-1">Memory Book unlock</p>
          <p className="text-sm text-white/75">Start collecting memories today — only <strong>£99</strong> when you're ready to create your Memory Book.</p>
          <p className="text-[11px] text-white/40 mt-1">One class · No subscription · Whole-class keepsake</p>
        </div>
      </div>

      {/* Helper for test reliability */}
      <a href={controlUrl} hidden>{controlUrl}</a>
    </div>
  );
}


function ParentShareCard({ event, slug, shareUrl }) {
  const schoolLine = event?.school_name ? ` at ${event.school_name}` : '';
  const parentMsg = `Hi everyone,\n\nWe've set up a Year 6 memory page for the class${schoolLine} 🎓\nChildren will be adding photos, messages and videos from their final weeks.\n\nYou'll be able to view and download a beautiful Memory Book at the end.\n\n👉 View here:\n${shareUrl}\n\n— Year 6 team`;

  const track = (channel) => {
    if (!event?.event_id) return;
    let anonId = null;
    try { anonId = localStorage.getItem('anon_id'); } catch { /* */ }
    axios.post(`${API}/events/${event.event_id}/year6/track-parent-share`, { channel, anon_id: anonId })
      .catch(() => { /* non-blocking */ });
  };

  const copyMsg = async () => {
    try { await navigator.clipboard.writeText(parentMsg); toast.success('Message copied — paste into your parents WhatsApp group'); track('copy'); }
    catch { toast.error('Could not copy'); }
  };
  const waUrl = `https://wa.me/?text=${encodeURIComponent(parentMsg)}`;
  const mailUrl = `mailto:?subject=${encodeURIComponent('Our Year 6 Memory Experience')}&body=${encodeURIComponent(parentMsg)}`;

  return (
    <div className="rounded-2xl p-5 mb-4" style={{ background: 'linear-gradient(135deg, rgba(250,204,21,0.10), rgba(245,158,11,0.04))', border: '1px solid rgba(250,204,21,0.25)' }} data-testid="year6-ready-parent-share">
      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-200 mb-2 inline-flex items-center gap-1.5">
        <UsersThree size={11} weight="fill" /> Share with parents 👇
      </p>
      <h3 className="font-bold text-lg mb-1">Turn 1 class into 30 families</h3>
      <p className="text-white/60 text-sm mb-3">Parents will be the ones downloading the Memory Book at the end. Send them this short message — they'll see what their kids are creating and want a copy of their own.</p>

      <pre className="rounded-xl bg-white/[0.04] border border-white/10 p-3.5 text-[12px] text-white/85 leading-relaxed whitespace-pre-wrap font-mono mb-3 overflow-x-auto" data-testid="year6-ready-parent-msg">{parentMsg}</pre>

      <div className="grid grid-cols-3 gap-2">
        <button onClick={copyMsg} className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-black bg-white text-neutral-900 hover:bg-neutral-100" data-testid="year6-ready-parent-copy">
          <Copy size={13} weight="bold" /> Copy
        </button>
        <a onClick={() => track('whatsapp')} href={waUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-black text-white" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }} data-testid="year6-ready-parent-whatsapp">
          <WhatsappLogo size={13} weight="fill" /> WhatsApp
        </a>
        <a onClick={() => track('email')} href={mailUrl} className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-black text-white" style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }} data-testid="year6-ready-parent-email">
          <EnvelopeSimple size={13} weight="bold" /> Email
        </a>
      </div>
    </div>
  );
}
